import React, { useState, useEffect } from 'react';
import { Icons } from './Icons';
import { useLanguage } from '../contexts/LanguageContext';
import { APIService } from '../services/api';

const FeatureVoting = ({ user, onClose }) => {
    const { t } = useLanguage();
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewForm, setShowNewForm] = useState(false);
    const [newFeature, setNewFeature] = useState({ title: '', description: '', category: 'feature' });
    const [submitting, setSubmitting] = useState(false);
    const [filter, setFilter] = useState('all');
    const [sortBy, setSortBy] = useState('votes');

    const categories = [
        { value: 'feature', label: t('featureVoting.categoryFeature') || 'New Feature', icon: Icons.Lightbulb },
        { value: 'integration', label: t('featureVoting.categoryIntegration') || 'Integration', icon: Icons.Link },
        { value: 'improvement', label: t('featureVoting.categoryImprovement') || 'Improvement', icon: Icons.TrendingUp },
        { value: 'tool', label: t('featureVoting.categoryTool') || 'New Tool', icon: Icons.Tool }
    ];

    const statusColors = {
        'open': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        'planned': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        'in_progress': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        'completed': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        'declined': 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
    };

    const statusLabels = {
        'open': t('featureVoting.statusOpen') || 'Open',
        'planned': t('featureVoting.statusPlanned') || 'Planned',
        'in_progress': t('featureVoting.statusInProgress') || 'In Progress',
        'completed': t('featureVoting.statusCompleted') || 'Completed',
        'declined': t('featureVoting.statusDeclined') || 'Declined'
    };

    useEffect(() => {
        loadFeatures();
    }, []);

    const loadFeatures = async () => {
        try {
            const data = await APIService.getFeatureRequests();
            setFeatures(data.features || []);
        } catch (error) {
            console.error('Failed to load features:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVote = async (featureId) => {
        if (!user) {
            alert(t('featureVoting.loginRequired') || 'Please log in to vote');
            return;
        }
        
        try {
            const result = await APIService.voteFeature(featureId);
            setFeatures(prev => prev.map(f => 
                f.id === featureId 
                    ? { ...f, votes_count: result.votes_count, user_voted: result.user_voted }
                    : f
            ));
        } catch (error) {
            console.error('Vote failed:', error);
        }
    };

    const handleSubmitFeature = async (e) => {
        e.preventDefault();
        if (!newFeature.title.trim()) return;
        
        setSubmitting(true);
        try {
            const result = await APIService.submitFeatureRequest(newFeature);
            setFeatures(prev => [result.feature, ...prev]);
            setNewFeature({ title: '', description: '', category: 'feature' });
            setShowNewForm(false);
        } catch (error) {
            console.error('Submit failed:', error);
            alert(error.message || 'Failed to submit feature request');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredFeatures = features
        .filter(f => filter === 'all' || f.category === filter || f.status === filter)
        .sort((a, b) => {
            if (sortBy === 'votes') return b.votes_count - a.votes_count;
            if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
            return 0;
        });

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Icons.Lightbulb className="w-5 h-5 text-yellow-500" />
                        {t('featureVoting.title') || 'Feature Requests'}
                    </h2>
                    {onClose && (
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                            <Icons.X className="w-5 h-5 text-gray-500" />
                        </button>
                    )}
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('featureVoting.subtitle') || 'Vote for features you want to see or suggest new ones'}
                </p>
                
                <div className="flex flex-wrap gap-2 items-center justify-between">
                    <div className="flex gap-2">
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                            <option value="all">{t('featureVoting.filterAll') || 'All'}</option>
                            <option value="open">{t('featureVoting.statusOpen') || 'Open'}</option>
                            <option value="planned">{t('featureVoting.statusPlanned') || 'Planned'}</option>
                            <option value="in_progress">{t('featureVoting.statusInProgress') || 'In Progress'}</option>
                        </select>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                            <option value="votes">{t('featureVoting.sortVotes') || 'Most Voted'}</option>
                            <option value="newest">{t('featureVoting.sortNewest') || 'Newest'}</option>
                        </select>
                    </div>
                    
                    {user && (
                        <button
                            onClick={() => setShowNewForm(!showNewForm)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            <Icons.Plus className="w-4 h-4" />
                            {t('featureVoting.suggest') || 'Suggest Feature'}
                        </button>
                    )}
                </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {showNewForm && (
                    <form onSubmit={handleSubmitFeature} className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-4">
                        <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                            {t('featureVoting.newFeature') || 'Suggest a Feature'}
                        </h3>
                        <input
                            type="text"
                            value={newFeature.title}
                            onChange={(e) => setNewFeature(prev => ({ ...prev, title: e.target.value }))}
                            placeholder={t('featureVoting.titlePlaceholder') || 'Feature title...'}
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-3"
                            required
                        />
                        <textarea
                            value={newFeature.description}
                            onChange={(e) => setNewFeature(prev => ({ ...prev, description: e.target.value }))}
                            placeholder={t('featureVoting.descriptionPlaceholder') || 'Describe why this would be useful...'}
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-3 h-20 resize-none"
                        />
                        <div className="flex gap-2 mb-3">
                            {categories.map(cat => (
                                <button
                                    key={cat.value}
                                    type="button"
                                    onClick={() => setNewFeature(prev => ({ ...prev, category: cat.value }))}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                        newFeature.category === cat.value
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    <cat.icon className="w-3.5 h-3.5" />
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setShowNewForm(false)}
                                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                            >
                                {t('common.cancel') || 'Cancel'}
                            </button>
                            <button
                                type="submit"
                                disabled={submitting || !newFeature.title.trim()}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
                            >
                                {submitting ? (t('common.submitting') || 'Submitting...') : (t('featureVoting.submit') || 'Submit')}
                            </button>
                        </div>
                    </form>
                )}
                
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Icons.Loader className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : filteredFeatures.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                        <Icons.Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t('featureVoting.empty') || 'No feature requests yet. Be the first to suggest one!'}</p>
                    </div>
                ) : (
                    filteredFeatures.map(feature => (
                        <div 
                            key={feature.id}
                            className="flex gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <button
                                onClick={() => handleVote(feature.id)}
                                disabled={!user}
                                className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl transition-all ${
                                    feature.user_voted
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                                } ${!user ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                            >
                                <Icons.ChevronUp className="w-5 h-5" />
                                <span className="text-sm font-bold">{feature.votes_count}</span>
                            </button>
                            
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <h3 className="font-medium text-gray-900 dark:text-white">
                                        {feature.title}
                                    </h3>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[feature.status]}`}>
                                        {statusLabels[feature.status]}
                                    </span>
                                </div>
                                {feature.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                        {feature.description}
                                    </p>
                                )}
                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-500">
                                    <span className="flex items-center gap-1">
                                        {categories.find(c => c.value === feature.category)?.icon && 
                                            React.createElement(categories.find(c => c.value === feature.category).icon, { className: 'w-3 h-3' })}
                                        {categories.find(c => c.value === feature.category)?.label || feature.category}
                                    </span>
                                    <span>•</span>
                                    <span>{new Date(feature.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            
            {!user && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('featureVoting.loginToVote') || 'Log in to vote or suggest features'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default FeatureVoting;
